'use client'

import React, { useState, useMemo, useCallback } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Search,
  FileX,
  AlertCircle
} from 'lucide-react'
import { cn } from '../../utils/cn'
// Note: These imports will be resolved to actual components in production
// and to mocks during testing via vitest configuration
import { BirhausButton } from '../BirhausButton'
import { BirhausInput } from '../BirhausInput' 
import { BirhausCheckbox } from '../BirhausCheckbox'
import { BirhausEmptyState } from '../BirhausEmptyState'
import type { 
  BirhausDataTableProps, 
  BirhausTableColumn, 
  BirhausBulkAction, 
  SortDirection,
  PaginationInfo 
} from './types'

/**
 * BirhausDataTable - Advanced data table component with BIRHAUS principles
 * 
 * Features:
 * - Spanish-first labels with English fallbacks
 * - Miller's Law compliance (max 7±2 items in pagination)
 * - Cognitive load reduction through progressive disclosure
 * - Accessibility-first design (WCAG AA+)
 * - Undo-over-confirm patterns (no confirmation dialogs)
 */
export function BirhausDataTable<T extends Record<string, any>>({
  // Data
  data,
  columns = [],
  keyField,
  
  // States
  loading = false,
  error = null,
  
  // Search
  searchPlaceholderEs = "Buscar...",
  searchPlaceholderEn = "Search...",
  globalSearch = true,
  onSearch,
  
  // Pagination
  totalCount,
  pageSize = 10,
  currentPage = 1,
  onPageChange,
  pageSizeOptions = [10, 25, 50, 100],
  onPageSizeChange,
  
  // Selection
  selectable = false,
  selectedItems = [],
  onSelectionChange,
  bulkActions = [],
  
  // Actions
  onRowClick,
  rowActions,
  
  // Export
  exportable = false,
  onExport,
  
  // Styling & Labels
  className,
  rowClassName,
  emptyMessageEs = "No se encontraron resultados",
  emptyMessageEn = "No results found",
  emptyIcon: EmptyIcon = FileX,
  locale = 'es'
}: BirhausDataTableProps<T>) {
  // BIRHAUS Principle: Defensive programming with safe defaults
  const safeColumns = useMemo(() => {
    if (!columns || !Array.isArray(columns)) return []
    return columns
  }, [columns])
  
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)
  const [localSearchTerm, setLocalSearchTerm] = useState('')

  // BIRHAUS Principle: Miller's Law - Limit pagination to 7±2 pages max
  const totalPages = totalCount ? Math.ceil(totalCount / pageSize) : 1
  const showPagination = totalCount && totalCount > pageSize
  const maxVisiblePages = Math.min(7, totalPages) // Miller's Law compliance

  // Handle sorting with BIRHAUS undo pattern
  const handleSort = useCallback((columnKey: string) => {
    if (sortColumn === columnKey) {
      if (sortDirection === null) {
        setSortDirection('asc')
      } else if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else {
        // BIRHAUS: Clear sort instead of showing confirmation
        setSortDirection(null)
        setSortColumn(null)
        return
      }
    } else {
      setSortColumn(columnKey)
      setSortDirection('asc')
    }
  }, [sortColumn, sortDirection])

  // Sort data with nested key support
  const sortedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return []
    if (!sortColumn || !sortDirection) return data

    return [...data].sort((a, b) => {
      const aValue = sortColumn.includes('.')
        ? sortColumn.split('.').reduce((obj, key) => obj?.[key], a)
        : a[sortColumn]
      const bValue = sortColumn.includes('.')
        ? sortColumn.split('.').reduce((obj, key) => obj?.[key], b)
        : b[sortColumn]

      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [data, sortColumn, sortDirection])

  // Filter data locally if no external search handler
  const filteredData = useMemo(() => {
    if (!globalSearch || onSearch || !localSearchTerm) return sortedData

    const searchableColumns = safeColumns.filter(col => col.searchable !== false)
    const term = localSearchTerm.toLowerCase()

    return sortedData.filter(item => {
      return searchableColumns.some(col => {
        const value = col.key.includes('.')
          ? col.key.split('.').reduce((obj, key) => obj?.[key], item)
          : item[col.key]
        
        return value?.toString().toLowerCase().includes(term)
      })
    })
  }, [sortedData, localSearchTerm, globalSearch, onSearch, safeColumns])

  // Selection handlers with BIRHAUS undo patterns
  const isAllSelected = selectable && selectedItems.length === filteredData.length && filteredData.length > 0
  const isPartiallySelected = selectable && selectedItems.length > 0 && selectedItems.length < filteredData.length

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      onSelectionChange?.([])
    } else {
      onSelectionChange?.(filteredData)
    }
  }, [isAllSelected, filteredData, onSelectionChange])

  const handleSelectItem = useCallback((item: T) => {
    const isSelected = selectedItems.some(
      selected => selected[keyField] === item[keyField]
    )
    
    if (isSelected) {
      onSelectionChange?.(
        selectedItems.filter(selected => selected[keyField] !== item[keyField])
      )
    } else {
      onSelectionChange?.([...selectedItems, item])
    }
  }, [selectedItems, keyField, onSelectionChange])

  // Render sort icon with BIRHAUS color scheme
  const SortIcon = ({ columnKey }: { columnKey: string }) => {
    if (sortColumn !== columnKey) {
      return <ChevronsUpDown className="w-4 h-4 text-gray-400" />
    }
    if (sortDirection === 'asc') {
      return <ChevronUp className="w-4 h-4 text-blue-600" />
    }
    return <ChevronDown className="w-4 h-4 text-blue-600" />
  }

  // Get localized labels
  const getLabel = (es: string, en?: string) => {
    return locale === 'es' ? es : (en || es)
  }

  // BIRHAUS Principle: Defensive error handling
  if (safeColumns.length === 0) {
    console.error('BirhausDataTable: columns prop is required and must be a non-empty array')
    return (
      <div className="p-8 text-center text-gray-500 border border-gray-200 rounded-lg">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p className="text-lg font-medium">
          {getLabel('Error de configuración', 'Configuration error')}
        </p>
        <p className="text-sm mt-2">
          {getLabel('No se pudieron cargar las columnas de la tabla', 'Could not load table columns')}
        </p>
      </div>
    )
  }

  // Loading state with skeleton
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded mb-4" />
          <div className="border border-gray-200 rounded-lg">
            {Array.from({ length: pageSize }).map((_, i) => (
              <div key={i} className="border-b border-gray-200 last:border-b-0 p-4">
                <div className="flex space-x-4">
                  {safeColumns.map((col, j) => (
                    <div key={j} className="h-4 bg-gray-200 rounded flex-1" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <BirhausEmptyState
        titleEs="Error al cargar datos"
        titleEn="Error loading data"
        descriptionEs={error}
        descriptionEn={error}
        icon={<AlertCircle className="w-12 h-12" />}
        error={true}
      />
    )
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        {globalSearch && (
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <BirhausInput
              type="text"
              placeholder={locale === 'es' ? searchPlaceholderEs : searchPlaceholderEn}
              value={localSearchTerm}
              onChange={(e) => {
                setLocalSearchTerm(e.target.value)
                onSearch?.(e.target.value)
              }}
              className="pl-10"
            />
          </div>
        )}

        {/* Legacy bulk actions - now handled by BulkActionBar when integrated */}
        {selectable && selectedItems.length > 0 && bulkActions.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {selectedItems.length} {getLabel(
                selectedItems.length === 1 ? 'seleccionado' : 'seleccionados',
                selectedItems.length === 1 ? 'selected' : 'selected'
              )}
            </span>
            {bulkActions.slice(0, 2).map((action, index) => (
              <BirhausButton
                key={index}
                variant={action.variant || 'secondary'}
                size="sm"
                onClick={() => action.onClick(selectedItems)}
              >
                {action.icon && <action.icon className="w-4 h-4 mr-1" />}
                {getLabel(action.labelEs, action.labelEn)}
              </BirhausButton>
            ))}
            {bulkActions.length > 2 && (
              <span className="text-sm text-gray-500">
                +{bulkActions.length - 2} {getLabel('más', 'more')}
              </span>
            )}
          </div>
        )}

        {/* Export functionality placeholder */}
        {exportable && onExport && (
          <BirhausButton
            variant="secondary"
            size="sm"
            onClick={() => onExport('csv', { data: filteredData, columns: safeColumns })}
          >
            {getLabel('Exportar', 'Export')}
          </BirhausButton>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr className="border-b border-gray-200">
              {selectable && (
                <th className="w-12 px-4 py-3">
                  <BirhausCheckbox
                    checked={isAllSelected}
                    indeterminate={isPartiallySelected}
                    onChange={handleSelectAll}
                    aria-label={getLabel('Seleccionar todos', 'Select all')}
                  />
                </th>
              )}
              {safeColumns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    "px-4 py-3 text-left text-sm font-medium text-gray-700",
                    column.sortable && "cursor-pointer hover:bg-gray-100 transition-colors",
                    column.headerClassName
                  )}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="flex items-center gap-1">
                    {getLabel(column.labelEs, column.labelEn)}
                    {column.sortable && <SortIcon columnKey={column.key} />}
                  </div>
                </th>
              ))}
              {rowActions && (
                <th className="w-12 px-4 py-3">
                  <span className="sr-only">{getLabel('Acciones', 'Actions')}</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {filteredData.length === 0 ? (
              <tr>
                <td
                  colSpan={safeColumns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)}
                  className="px-4 py-12 text-center"
                >
                  <BirhausEmptyState
                    icon={<EmptyIcon className="w-12 h-12" />}
                    titleEs={emptyMessageEs}
                    titleEn={emptyMessageEn}
                    size="sm"
                  />
                </td>
              </tr>
            ) : (
              filteredData.map((item, index) => {
                const isSelected = selectedItems.some(
                  selected => selected[keyField] === item[keyField]
                )
                
                return (
                  <tr
                    key={item[keyField]}
                    onClick={() => onRowClick?.(item)}
                    className={cn(
                      "hover:bg-gray-50 transition-colors",
                      onRowClick && "cursor-pointer",
                      isSelected && "bg-blue-50",
                      rowClassName?.(item, index)
                    )}
                  >
                    {selectable && (
                      <td className="w-12 px-4 py-3">
                        <BirhausCheckbox
                          checked={isSelected}
                          onChange={() => handleSelectItem(item)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={getLabel(
                            `Seleccionar fila ${index + 1}`,
                            `Select row ${index + 1}`
                          )}
                        />
                      </td>
                    )}
                    {safeColumns.map((column) => (
                      <td
                        key={column.key}
                        className={cn("px-4 py-3 text-sm", column.className)}
                      >
                        {column.render
                          ? column.render(item, index)
                          : column.key.includes('.')
                            ? column.key.split('.').reduce((obj, key) => obj?.[key], item)
                            : item[column.key]
                        }
                      </td>
                    ))}
                    {rowActions && (
                      <td className="w-12 px-4 py-3">
                        <div onClick={(e) => e.stopPropagation()}>
                          {rowActions(item)}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination - BIRHAUS Miller's Law compliant */}
      {showPagination && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {getLabel(
                `Mostrando ${((currentPage - 1) * pageSize) + 1} - ${Math.min(currentPage * pageSize, totalCount || 0)} de ${totalCount}`,
                `Showing ${((currentPage - 1) * pageSize) + 1} - ${Math.min(currentPage * pageSize, totalCount || 0)} of ${totalCount}`
              )}
            </span>
            {onPageSizeChange && (
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="ml-2 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>
                    {size} {getLabel('por página', 'per page')}
                  </option>
                ))}
              </select>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <BirhausButton
              variant="ghost"
              size="sm"
              onClick={() => onPageChange?.(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4" />
              {getLabel('Anterior', 'Previous')}
            </BirhausButton>
            
            {/* BIRHAUS: Miller's Law - Max 7±2 page buttons */}
            <div className="flex items-center gap-1">
              {Array.from({ length: maxVisiblePages }, (_, i) => {
                let pageNum
                if (totalPages <= maxVisiblePages) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - maxVisiblePages + 1 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                
                return (
                  <BirhausButton
                    key={pageNum}
                    variant={pageNum === currentPage ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => onPageChange?.(pageNum)}
                    className="min-w-[32px]"
                  >
                    {pageNum}
                  </BirhausButton>
                )
              })}
            </div>
            
            <BirhausButton
              variant="ghost"
              size="sm"
              onClick={() => onPageChange?.(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              {getLabel('Siguiente', 'Next')}
              <ChevronRight className="w-4 h-4" />
            </BirhausButton>
          </div>
        </div>
      )}
    </div>
  )
}

// Hook for managing table state
export function useBirhausDataTable<T extends Record<string, any>>(initialData: T[] = []) {
  const [data, setData] = useState<T[]>(initialData)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedItems, setSelectedItems] = useState<T[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  
  const resetSelection = useCallback(() => {
    setSelectedItems([])
  }, [])
  
  const resetPagination = useCallback(() => {
    setCurrentPage(1)
  }, [])
  
  return {
    // Data state
    data,
    setData,
    loading,
    setLoading,
    error,
    setError,
    
    // Selection state
    selectedItems,
    setSelectedItems,
    resetSelection,
    
    // Pagination state
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    resetPagination,
    
    // Search state
    searchTerm,
    setSearchTerm,
    
    // Computed values
    totalCount: data.length,
    hasSelection: selectedItems.length > 0,
    paginationInfo: {
      currentPage,
      pageSize,
      totalCount: data.length,
      totalPages: Math.ceil(data.length / pageSize),
      hasNextPage: currentPage < Math.ceil(data.length / pageSize),
      hasPreviousPage: currentPage > 1
    } as PaginationInfo
  }
}

export { BirhausDataTable as default }